/**
 * @file Renders the modal that switches between the SMS and authenticator
 * two factor setup methods.
 */
import React, { useState } from "react";
import { Tabs } from "antd";

import { Modal } from "@components/Page";
import type { Operation } from "@components/AppContext/types";

import MobileVerificationRequest from "./MobileVerificationRequest";
import TotpSetup from "./TotpSetup";

const LABEL_TITLE = "Two Factor Authentication";
const LABEL_SMS = "SMS";
const LABEL_TOTP = "Authenticator App";
const SMS_METHOD = "SMS_MFA";
const TOTP_METHOD = "SOFTWARE_TOKEN_MFA";

type ModalControls = {
  open?: () => void;
}

type ModalRef = {
  current: ModalControls;
}

type Props = {
  modal: ModalRef;
  mobileNumber?: string;
  mfaMethods?: string[];
  isDisabled?: boolean;
  verificationEnableOperation?: Operation;
  onSuccess: () => Promise<void> | void;
}

/**
 * Renders the SMS and authenticator tabs for setting up two factor.
 */
const TwoFactorAuthenticationModal = ({
  modal,
  mobileNumber,
  mfaMethods = [],
  isDisabled = false,
  verificationEnableOperation,
  onSuccess,
}: Props) => {
  const [ isOpen, setIsOpen ] = useState(false);
  const [ tabsRenderKey, setTabsRenderKey ] = useState(0);
  
  const hasSmsMethod = mfaMethods.includes(SMS_METHOD);
  const hasTotpMethod = mfaMethods.includes(TOTP_METHOD);
  
  const initialMethod = hasTotpMethod && !hasSmsMethod
    ? TOTP_METHOD
    : SMS_METHOD;
  
  const [ selectedMethod, setSelectedMethod ] = useState(initialMethod);

  /**
   * Opens the modal.
   */
  modal.current.open = () =>
    setIsOpen(true);

  /**
   * Closes the modal and resets the tabs to their initial method.
   */
  const onClose = () => {
    setTabsRenderKey((renderKey) => renderKey + 1);
    setIsOpen(false);
  };

  /**
   * Reports success to the caller, then closes the modal.
   */
  const onActivateSuccess = async () => {
    await onSuccess();
    onClose();
  };

  const tabsItems = [
    {
      key: SMS_METHOD,
      label: LABEL_SMS,
      disabled: isDisabled,
      children: (
        <MobileVerificationRequest
          mobileNumber={mobileNumber}
          enableOperation={verificationEnableOperation}
          onSuccess={onActivateSuccess}
        />
      ),
    },
    {
      key: TOTP_METHOD,
      label: LABEL_TOTP,
      disabled: isDisabled,
      children: (
        <TotpSetup onSuccess={onActivateSuccess} />
      ),
    },
  ];

  return (
    <Modal
      title={LABEL_TITLE}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Tabs
        key={tabsRenderKey}
        items={tabsItems}
        onChange={setSelectedMethod}
        activeKey={selectedMethod}
      />
    </Modal>
  );
};

export default TwoFactorAuthenticationModal;
